"use client";

import { c as _c } from "react/compiler-runtime";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ReactNode, useCallback, useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useParams } from "next/navigation";
import { Button, Input } from "@/components/ui";
import { Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { SelfSchedulePatientDetailsContext } from "@/contexts/self-schedule-patient-details-context";
import { patientClaimQueryKey, usePatientClaim, useUpdateClaimedPatient } from "@/hooks/patient-claim/use-patient-claim";
import { useClinicClock } from "@/hooks/use-clinic-clock";
import { parseError } from "@/lib/parse-error";
import { PATIENT_PERMISSIONS, hasPatientPermission } from "@/lib/patient-permissions";
import { usesSelfSchedulePatientDetails } from "@/lib/tenant-flags";
import { SelfSchedulePatient } from "@/types/self-schedule";
import { SelfSchedulePatientDetails, updateSelfSchedulePatientDetails } from "@/api/check-in/self-schedule/patient-details";
import { tenantQueryOptions } from "@/query/tenant";

/** Keep booking/auth state mounted while a blocking dialog collects details. */
export function PatientDetailsGate({
  children
}: {
  children: ReactNode;
}) {
  const {
    claimedPatient: patient,
    firebaseUid
  } = usePatientClaim();
  const {
    tenant
  } = useParams<{
    tenant: string;
  }>();
  const {
    data: publicTenant
  } = useQuery(tenantQueryOptions.public(tenant));
  const enabled = usesSelfSchedulePatientDetails(tenant);
  const permissionsReady = publicTenant != null;
  const canWriteProfile = enabled && permissionsReady && hasPatientPermission(publicTenant, PATIENT_PERMISSIONS.profile.write);
  const identity = patient ? `${tenant}:${firebaseUid}:${patient.id}` : null;
  const [completedIdentity, setCompletedIdentity] = useState<string | null>(null);
  const continuation = useRef<{
    patientId: string;
    run: () => void;
  } | null>(null);
  useEffect(() => {
    setCompletedIdentity(null);
  }, [identity]);
  const ready = identity !== null && (!enabled || permissionsReady && (!canWriteProfile || identity === completedIdentity));
  useEffect(() => {
    const pending = continuation.current;
    if (ready && pending && pending.patientId === patient?.id) {
      continuation.current = null;
      pending.run();
    }
  }, [ready, patient?.id]);
  const latest = useRef({
    ready,
    patientId: patient?.id
  });
  latest.current = {
    ready,
    patientId: patient?.id
  };
  const afterDetails = useCallback((patientId: string, run: () => void) => {
    // Auth callbacks may have started before Firebase switched UIDs. Read the
    // current gate, rather than capturing that earlier signed-out identity.
    if (latest.current.ready && latest.current.patientId === patientId) run();else continuation.current = {
      patientId,
      run
    };
  }, []);
  return <SelfSchedulePatientDetailsContext.Provider value={{
    ready,
    afterDetails
  }}>
      {children}
      <Dialog open={!!patient && canWriteProfile && !ready}>
        <DialogContent className="sm:max-w-md" onEscapeKeyDown={event => event.preventDefault()} onInteractOutside={event_0 => event_0.preventDefault()}>
          {patient && canWriteProfile ? <PatientDetailsForm key={identity} patient={patient} onComplete={() => {
          setCompletedIdentity(identity);
        }} /> : null}
        </DialogContent>
      </Dialog>
    </SelfSchedulePatientDetailsContext.Provider>;
}
export function PatientDetailsForm(t0) {
  const $ = _c(84);
  const {
    patient,
    onComplete
  } = t0;
  const clock = useClinicClock();
  const updateClaim = useUpdateClaimedPatient();
  const {
    firebaseUid
  } = usePatientClaim();
  const queryClient = useQueryClient();
  const [saveError, setSaveError] = useState(null);
  const t1 = patient.phoneNumber ? undefined : "";
  const t2 = patient.email ? undefined : "";
  const t3 = patient.zipCode ?? "";
  const t4 = patient.dateOfBirth ?? "";
  const t5 = patient.leadSource ? undefined : "";
  let t6;
  if ($[0] !== t1 || $[1] !== t2 || $[2] !== t3 || $[3] !== t4 || $[4] !== t5) {
    t6 = {
      defaultValues: {
        phoneNumber: t1,
        email: t2,
        zipCode: t3,
        dateOfBirth: t4,
        leadSource: t5
      }
    };
    $[0] = t1;
    $[1] = t2;
    $[2] = t3;
    $[3] = t4;
    $[4] = t5;
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  const {
    register,
    handleSubmit,
    formState: t7
  } = useForm(t6);
  const {
    errors
  } = t7;
  let t8;
  if ($[6] !== patient.email || $[7] !== patient.id || $[8] !== patient.leadSource || $[9] !== patient.phoneNumber) {
    t8 = details => {
      const {
        phoneNumber,
        email,
        leadSource,
        ...profile
      } = details;
      return updateSelfSchedulePatientDetails(patient.id, {
        ...profile,
        ...(!patient.phoneNumber ? {
          phoneNumber
        } : {}),
        ...(!patient.email ? {
          email: email?.trim()
        } : {}),
        zipCode: details.zipCode.trim(),
        dateOfBirth: details.dateOfBirth || null,
        ...(!patient.leadSource ? {
          leadSource: leadSource?.trim() || null
        } : {})
      });
    };
    $[6] = patient.email;
    $[7] = patient.id;
    $[8] = patient.leadSource;
    $[9] = patient.phoneNumber;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  let t9;
  if ($[11] !== firebaseUid || $[12] !== onComplete || $[13] !== queryClient || $[14] !== updateClaim) {
    t9 = async updated => {
      await queryClient.cancelQueries({
        queryKey: patientClaimQueryKey(firebaseUid),
        exact: true
      });
      updateClaim(updated);
      onComplete();
    };
    $[11] = firebaseUid;
    $[12] = onComplete;
    $[13] = queryClient;
    $[14] = updateClaim;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = async error => setSaveError(await parseError(error));
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  let t11;
  if ($[17] !== t8 || $[18] !== t9) {
    t11 = {
      mutationFn: t8,
      onSuccess: t9,
      onError: t10
    };
    $[17] = t8;
    $[18] = t9;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  const mutation = useMutation(t11);
  let t12;
  if ($[20] !== mutation) {
    t12 = details_0 => {
      setSaveError(null);
      mutation.mutate(details_0);
    };
    $[20] = mutation;
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  let t13;
  if ($[22] !== handleSubmit || $[23] !== t12) {
    t13 = handleSubmit(t12);
    $[22] = handleSubmit;
    $[23] = t12;
    $[24] = t13;
  } else {
    t13 = $[24];
  }
  let t14;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <DialogHeader className="shrink-0"><DialogTitle>Complete your profile</DialogTitle><DialogDescription className="text-body text-foreground-secondary">Confirm your details to continue booking your visit.</DialogDescription></DialogHeader>;
    $[25] = t14;
  } else {
    t14 = $[25];
  }
  let t15;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <Label htmlFor="booking-phone" mandatory={true}>Phone number</Label>;
    $[26] = t15;
  } else {
    t15 = $[26];
  }
  const t16 = !!patient.phoneNumber;
  const t17 = patient.phoneNumber ? -1 : undefined;
  let t18;
  if ($[27] !== patient.phoneNumber || $[28] !== register) {
    t18 = patient.phoneNumber ? {
      value: patient.phoneNumber
    } : register("phoneNumber", {
      required: "Phone number is required",
      validate: _temp
    });
    $[27] = patient.phoneNumber;
    $[28] = register;
    $[29] = t18;
  } else {
    t18 = $[29];
  }
  const t19 = !!errors.phoneNumber;
  const t20 = errors.phoneNumber?.message;
  let t21;
  if ($[30] !== t16 || $[31] !== t17 || $[32] !== t18 || $[33] !== t19 || $[34] !== t20) {
    t21 = <div className="space-y-1">{t15}<Input id="booking-phone" type="tel" autoComplete="tel" readOnly={t16} tabIndex={t17} className="read-only:border-transparent read-only:px-0 read-only:py-1 read-only:shadow-none read-only:text-foreground-secondary" {...t18} hasError={t19} errorMessage={t20} /></div>;
    $[30] = t16;
    $[31] = t17;
    $[32] = t18;
    $[33] = t19;
    $[34] = t20;
    $[35] = t21;
  } else {
    t21 = $[35];
  }
  let t22;
  if ($[36] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = <Label htmlFor="booking-email" mandatory={true}>Email</Label>;
    $[36] = t22;
  } else {
    t22 = $[36];
  }
  const t23 = !!patient.email;
  const t24 = patient.email ? -1 : undefined;
  let t25;
  if ($[37] !== patient.email || $[38] !== register) {
    t25 = patient.email ? {
      value: patient.email
    } : register("email", {
      required: "Email is required",
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: "Enter a valid email address"
      }
    });
    $[37] = patient.email;
    $[38] = register;
    $[39] = t25;
  } else {
    t25 = $[39];
  }
  const t26 = !!errors.email;
  const t27 = errors.email?.message;
  let t28;
  if ($[40] !== t23 || $[41] !== t24 || $[42] !== t25 || $[43] !== t26 || $[44] !== t27) {
    t28 = <div className="space-y-1">{t22}<Input id="booking-email" type="email" autoComplete="email" readOnly={t23} tabIndex={t24} className="read-only:border-transparent read-only:px-0 read-only:py-1 read-only:shadow-none read-only:text-foreground-secondary" {...t25} hasError={t26} errorMessage={t27} /></div>;
    $[40] = t23;
    $[41] = t24;
    $[42] = t25;
    $[43] = t26;
    $[44] = t27;
    $[45] = t28;
  } else {
    t28 = $[45];
  }
  let t29;
  if ($[46] !== patient.email || $[47] !== patient.phoneNumber) {
    t29 = patient.phoneNumber || patient.email ? <p className="text-caption text-foreground-secondary">Contact the clinic to change contact details already on file.</p> : null;
    $[46] = patient.email;
    $[47] = patient.phoneNumber;
    $[48] = t29;
  } else {
    t29 = $[48];
  }
  let t30;
  if ($[49] === Symbol.for("react.memo_cache_sentinel")) {
    t30 = <Label htmlFor="booking-zip" mandatory={true}>ZIP code</Label>;
    $[49] = t30;
  } else {
    t30 = $[49];
  }
  let t31;
  if ($[50] === Symbol.for("react.memo_cache_sentinel")) {
    t31 = /^\d{5}(-\d{4})?$/;
    $[50] = t31;
  } else {
    t31 = $[50];
  }
  let t32;
  if ($[51] !== register) {
    t32 = register("zipCode", {
      required: "ZIP code is required",
      pattern: {
        value: t31,
        message: "Enter a valid ZIP code"
      }
    });
    $[51] = register;
    $[52] = t32;
  } else {
    t32 = $[52];
  }
  const t33 = !!errors.zipCode;
  const t34 = errors.zipCode?.message;
  let t35;
  if ($[53] !== t32 || $[54] !== t33 || $[55] !== t34) {
    t35 = <div className="space-y-1">{t30}<Input id="booking-zip" autoComplete="postal-code" inputMode="numeric" {...t32} hasError={t33} errorMessage={t34} /></div>;
    $[53] = t32;
    $[54] = t33;
    $[55] = t34;
    $[56] = t35;
  } else {
    t35 = $[56];
  }
  let t36;
  if ($[57] === Symbol.for("react.memo_cache_sentinel")) {
    t36 = <Label htmlFor="booking-dob">Date of birth (optional)</Label>;
    $[57] = t36;
  } else {
    t36 = $[57];
  }
  let t37;
  if ($[58] !== clock) {
    t37 = clock.today();
    $[58] = clock;
    $[59] = t37;
  } else {
    t37 = $[59];
  }
  let t38;
  if ($[60] !== register) {
    t38 = register("dateOfBirth");
    $[60] = register;
    $[61] = t38;
  } else {
    t38 = $[61];
  }
  let t39;
  if ($[62] !== t37 || $[63] !== t38) {
    t39 = <div className="space-y-1">{t36}<Input id="booking-dob" type="date" autoComplete="bday" max={t37} {...t38} /></div>;
    $[62] = t37;
    $[63] = t38;
    $[64] = t39;
  } else {
    t39 = $[64];
  }
  let t40;
  if ($[65] !== patient.leadSource || $[66] !== register) {
    t40 = !patient.leadSource ? <div className="space-y-1"><Label htmlFor="booking-source">How did you hear about us? (optional)</Label><Input id="booking-source" maxLength={255} placeholder="For example, Google or a friend" {...register("leadSource")} /></div> : null;
    $[65] = patient.leadSource;
    $[66] = register;
    $[67] = t40;
  } else {
    t40 = $[67];
  }
  let t41;
  if ($[68] !== saveError) {
    t41 = saveError ? <p role="alert" className="text-status-error-text">{saveError}</p> : null;
    $[68] = saveError;
    $[69] = t41;
  } else {
    t41 = $[69];
  }
  let t42;
  if ($[70] !== t21 || $[71] !== t28 || $[72] !== t29 || $[73] !== t35 || $[74] !== t39 || $[75] !== t40 || $[76] !== t41) {
    t42 = <DialogBody className="space-y-4">{t21}{t28}{t29}{t35}{t39}{t40}{t41}</DialogBody>;
    $[70] = t21;
    $[71] = t28;
    $[72] = t29;
    $[73] = t35;
    $[74] = t39;
    $[75] = t40;
    $[76] = t41;
    $[77] = t42;
  } else {
    t42 = $[77];
  }
  let t43;
  if ($[78] !== mutation.isPending) {
    t43 = <DialogFooter className="shrink-0"><Button type="submit" variant="primary" className="min-h-11 w-full" isLoading={mutation.isPending} loadingText={"Saving\u2026"}>Continue</Button></DialogFooter>;
    $[78] = mutation.isPending;
    $[79] = t43;
  } else {
    t43 = $[79];
  }
  let t44;
  if ($[80] !== t13 || $[81] !== t42 || $[82] !== t43) {
    t44 = <form className="flex min-h-0 flex-1 flex-col text-foreground" onSubmit={t13}>{t14}{t42}{t43}</form>;
    $[80] = t13;
    $[81] = t42;
    $[82] = t43;
    $[83] = t44;
  } else {
    t44 = $[83];
  }
  return t44;
}
function _temp(value) {
  return /^(1)?\d{10}$/.test((value ?? "").replace(/\D/g, "")) || "Enter a valid 10-digit phone number";
}